import { and, eq } from "drizzle-orm";
import { isArray, isObject, size } from "lodash";

import type { Client, WsAppSyncEvent } from "@storyboard/web-client";
import { Timings } from "@storyboard/web-logger/timings/Timings";
import { createLogger } from "@/logger";

const log = createLogger("appsync");

import { flags } from "@/config";
import { db } from "@/db/db";
import { workspaceMembership } from "@/db/schema";
import { initializeIsAliasFeedFromIds } from "@/models/actions/initialFeedLoad.tsx";
import { getQueueKey, mergeEvents, queues } from "@/models/appSyncFunctions.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { bulkUpsertAccount } from "@/models/repositories/Account";
import { bulkUpsertAccountEvent } from "@/models/repositories/AccountEvent";
import { bulkUpsertWsAudioEncodings } from "@/models/repositories/AudioEncoding";
import { bulkUpsertBroadcastAction } from "@/models/repositories/BroadcastAction";
import { bulkUpsertBroadcastRecipients } from "@/models/repositories/BroadcastRecipient";
import { bulkUpsertCallConfiguration } from "@/models/repositories/CallConfiguration";
import { bulkUpsertCallRecord } from "@/models/repositories/CallRecord";
import { bulkUpsertDeviceRegistration } from "@/models/repositories/DeviceRegistration";
import { bulkUpsertDirectWsInvitation } from "@/models/repositories/DirectWsInvitation";
import { bulkUpsertDisplayArtifact } from "@/models/repositories/DisplayArtifact";
import { bulkUpsertFeed } from "@/models/repositories/Feed";
import { bulkUpsertFeedGroup } from "@/models/repositories/FeedGroup";
import { bulkUpsertFeedGroupMembership } from "@/models/repositories/FeedGroupMembership";
import { bulkUpsertFeedPreference } from "@/models/repositories/FeedPreference";
import { bulkUpsertFile } from "@/models/repositories/File";
import { bulkUpsertItemAndCalculateMembership } from "@/models/repositories/Item";
import { bulkUpsertLink } from "@/models/repositories/Link";
import { bulkUpsertPermission } from "@/models/repositories/Permission";
import { bulkUpsertPipelineArtifactMetadata } from "@/models/repositories/PipelineArtifactMetadata";
import { bulkUpsertPublishedWorkflowItem } from "@/models/repositories/PublishedWorkflowItem";
import { bulkUpsertScheduleTrigger } from "@/models/repositories/ScheduleTrigger";
import { bulkUpsertTag } from "@/models/repositories/Tag";
import { bulkUpsertTemplate } from "@/models/repositories/Template";
import { bulkUpsertWsTranscriptions } from "@/models/repositories/Transcription";
import { bulkUpsertWorkflowItem } from "@/models/repositories/WorkflowItem";
import { bulkUpsertWorkspace } from "@/models/repositories/Workspace";
import { bulkUpsertWorkspaceCommandAlias } from "@/models/repositories/WorkspaceCommandAlias";
import { bulkUpsertWorkspaceMembership } from "@/models/repositories/WorkspaceMembership";
import { bulkUpsertWorkspaceMembershipTag } from "@/models/repositories/WorkspaceMembershipTag";
import { bulkUpsertWsMembershipAvailability } from "@/models/repositories/WsMembershipAvailability";
import type PubSub from "@/PubSub";
import { handleEchoResponse, registerAppSyncEcho } from "./AppSyncEcho";
import { enqueueCalculateUnreads } from "./actions/calculateUnreads";

const MIN_QUEUE_INTERVAL_MS = 1000;
const MAX_QUEUE_INTERVAL_MS = 10000;
let isProcessing = false;

function scheduleNextQueueProcess(delayMs: number): void {
  setTimeout(processQueues, Math.min(Math.max(delayMs, MIN_QUEUE_INTERVAL_MS), MAX_QUEUE_INTERVAL_MS));
}

async function processQueues(): Promise<void> {
  if (isProcessing) {
    return;
  }

  isProcessing = true;
  const start = performance.now();

  try {
    for (const [key, queueData] of queues.entries()) {
      if (queueData.events.length === 0) continue;

      const eventsToProcess = queueData.events;
      queueData.events = [];

      const mergedEvent = mergeEvents(eventsToProcess);
      await appSyncSubscriptionUpdate(mergedEvent, queueData.accountId, queueData.workspaceId);
    }
  } finally {
    isProcessing = false;
    const elapsed = performance.now() - start;
    scheduleNextQueueProcess(elapsed * 2);
  }
}

scheduleNextQueueProcess(MIN_QUEUE_INTERVAL_MS);

export async function appSyncSubscriptionUpdate(event: WsAppSyncEvent, myAccountId: string, workspaceId: string) {
  // Handle echo verification
  if (event?.asyncEchos?.length > 0) {
    for (const echo of event.asyncEchos) {
      handleEchoResponse(echo.data);
    }
  }

  const counts: Record<string, number> = Object.entries(event).reduce((memo, [key, val]) => {
    const cnt = isArray(val) || isObject(val) ? size(val) : 1;

    if (cnt > 0) {
      memo[key] = cnt;
    }

    return memo;
  }, {});

  const timingSpans = Object.entries(counts).map(([key, count]) =>
    Timings.startSpan(`AppSync - ${key}`, {
      category: "AppSync",
      properties: { count },
    }),
  );

  if (event?.accounts?.length) await bulkUpsertAccount(event.accounts);
  if (event?.workspaces?.length) await bulkUpsertWorkspace(event.workspaces);
  if (event?.workspaceMemberships?.length) await bulkUpsertWorkspaceMembership(event.workspaceMemberships);
  if (event?.workspaceMembershipTags?.length) await bulkUpsertWorkspaceMembershipTag(event.workspaceMembershipTags);
  if (event?.workspaceTags?.length) await bulkUpsertTag(event.workspaceTags);

  if (event?.directWorkspaceInvitations?.length) await bulkUpsertDirectWsInvitation(event.directWorkspaceInvitations);
  if (event?.feeds?.length) await bulkUpsertFeed(event.feeds);
  if (event?.feedGroups?.length) await bulkUpsertFeedGroup(event.feedGroups);
  if (event?.feedGroupMemberships?.length) await bulkUpsertFeedGroupMembership(event.feedGroupMemberships);
  if (event?.permissions?.length) await bulkUpsertPermission(event.permissions);
  if (event?.displayArtifacts?.length) await bulkUpsertDisplayArtifact(event.displayArtifacts);
  if (event?.files?.length) await bulkUpsertFile(event.files);
  if (event?.links?.length) await bulkUpsertLink(event.links);
  if (event?.commandAliases?.length) await bulkUpsertWorkspaceCommandAlias(event.commandAliases);
  if (event?.workflowItems?.length) await bulkUpsertWorkflowItem(event.workflowItems);
  if (event?.scheduleTriggers?.length) await bulkUpsertScheduleTrigger(event.scheduleTriggers);
  if (event?.broadcastActions?.length) await bulkUpsertBroadcastAction(event.broadcastActions);
  if (event?.publishedWorkflowItems?.length) await bulkUpsertPublishedWorkflowItem(event.publishedWorkflowItems);
  if (event?.broadcastRecipients?.length) await bulkUpsertBroadcastRecipients(event.broadcastRecipients);
  if (event?.pam?.length) await bulkUpsertPipelineArtifactMetadata(event.pam);
  if (event?.templates?.length) await bulkUpsertTemplate(event.templates);
  if (event?.deviceRegistrations?.length) await bulkUpsertDeviceRegistration(event.deviceRegistrations);

  if (event?.feedPreferences?.length > 0) {
    await bulkUpsertFeedPreference(event?.feedPreferences);
  }

  await bulkUpsertAccountEvent(event?.events);

  if (event?.membershipAvailabilities?.length > 0) {
    await bulkUpsertWsMembershipAvailability(event?.membershipAvailabilities);
  }

  if (event?.audioEncodings?.length > 0) {
    await bulkUpsertWsAudioEncodings(event?.audioEncodings);
  }

  if (event?.transcriptions?.length > 0) {
    await bulkUpsertWsTranscriptions(event?.transcriptions);
  }

  if (event?.callRecords?.length > 0) {
    await bulkUpsertCallRecord(event?.callRecords);
  }

  if (flags.allowInboundCalls && event?.callConfiguration) {
    await bulkUpsertCallConfiguration([event.callConfiguration]);
  }

  let itemsUpserted = [];
  if (event?.items?.length > 0) {
    log.info("[APPSYNC] bulkUpsertItem", event.items.length);
    itemsUpserted = await bulkUpsertItemAndCalculateMembership(event.items, { fromAppSync: false });
  }

  const feedIdsFromItems =
    itemsUpserted
      .flat()
      .filter((i) => i.firstInsert)
      .map((f) => f.feedId)
      ?.filter((id) => id) || [];

  const feedIdsFromEvents = event?.events?.map((f) => f.feedId)?.filter((id) => id) || [];

  const uniqueFeedIds = Array.from(new Set([...feedIdsFromItems, ...feedIdsFromEvents]));

  for (const timingSpan of timingSpans) {
    Timings.endSpan(timingSpan);
  }

  log.info("[APPSYNC] operations processed serially");

  const membership = await db.query.workspaceMembership.findFirst({
    where: and(eq(workspaceMembership.accountId, myAccountId), eq(workspaceMembership.workspaceId, workspaceId)),
  });

  if (event?.feeds?.length > 0) {
    await initializeIsAliasFeedFromIds(event.feeds.map((f) => f.id));
    emitTableUpdates(["feed"]);
  }

  if (membership && uniqueFeedIds.length > 0) {
    enqueueCalculateUnreads({
      membership,
      feedIds: uniqueFeedIds,
      alsoSetAsRead: true,
    });
  }
}

let subscription: any;

export async function subscribeToAppSync({
  accountId,
  currentWorkspaceId,
  pubSub,
  client,
  deviceRegistrationId,
}: {
  accountId: string;
  currentWorkspaceId: string;
  deviceRegistrationId: string;
  pubSub: PubSub;
  client: Client;
}) {
  if (!pubSub || !client) return;
  if (!accountId) return;

  const wkId = currentWorkspaceId || "";
  const key = getQueueKey(accountId, wkId);

  // Ensure there's a queue for this accountId/workspaceId pair
  if (!queues.has(key)) {
    queues.set(key, {
      accountId,
      workspaceId: wkId,
      events: [],
    });
  }

  const channels: string[] = [];
  channels.push(`wsaccount#${accountId}`);
  if (currentWorkspaceId) {
    channels.push(`workspace#${currentWorkspaceId}`);
    channels.push(`workspace_dispatch#${currentWorkspaceId}`);
  }
  const newSub = pubSub.subscribeFilter(
    channels,
    (data?: any) => {
      if (data?.data) {
        const eventData = JSON.parse(data.data) as WsAppSyncEvent;
        const queue = queues.get(key);
        if (queue) {
          const counts = Object.entries(eventData).reduce((memo, [key, val]) => {
            const cnt = isArray(val) || isObject(val) ? size(val) : 1;

            if (cnt > 0) {
              memo[key] = cnt;
            }

            return memo;
          }, {});

          for (const [key, count] of Object.entries(counts)) {
            Timings.createEvent(`AppSync Queue - ${key}`, {
              category: "AppSync",
              properties: { count },
            });
          }

          queue.events.push(eventData);
        } else {
          console.error(`No queue found for accountId=${accountId}, workspaceId=${currentWorkspaceId}`);
        }
      } else {
        console.warn("Account Feed Subscription Event called with no data", data);
      }
    },
    (e: any) => {
      console.error("Account Feed Subscription Error", e);
    },
  );
  subscription?.unsubscribe();
  subscription = newSub;

  registerAppSyncEcho(client, accountId, currentWorkspaceId, deviceRegistrationId);
}
